import { Fragment } from "react";
import GeoJSONPolyline from "geojson-polyline";
import { getLegAirport, getLegTerminal } from "../../utilities/flightBriefLegUtils";
import { mapboxConfig } from "../../utilities/settings";

const isReturnFlight = (briefData) => {
    if (!briefData.legs || briefData.legs.length !== 2) {
        return false;
    }

    const firstDeparture = briefData.departureAirport;
    const lastArrival = getLegAirport(briefData, 1, "arrival");

    return !!(firstDeparture && lastArrival && firstDeparture.icao === lastArrival.icao);
};

const getLatLng = (value, toArray) => {
    const [lat, lng] = value.split(",");

    return toArray ? [lng, lat] : `${lng},${lat}`;
};

const getLocationLatLng = (airport, terminal) => {
    if (terminal?.longitude_and_Latitude && terminal.longitude_and_Latitude !== "TBC") {
        return terminal.longitude_and_Latitude;
    }

    if (airport?.latitude && airport?.longitude) {
        return `${airport.latitude},${airport.longitude}`;
    }

    return null;
};

const getLegMapContext = (briefData, legOrIndex) => {
    if (typeof legOrIndex === "object" && legOrIndex !== null) {
        return {
            departureAirport: legOrIndex.departureAirport || legOrIndex.fromAirport,
            arrivalAirport: legOrIndex.arrivalAirport || legOrIndex.toAirport,
            departureTerminal: legOrIndex.departureTerminal,
            arrivalTerminal: legOrIndex.arrivalTerminal,
        };
    }

    const legIndex = typeof legOrIndex === "number" ? legOrIndex : 0;

    return {
        departureAirport: getLegAirport(briefData, legIndex, "departure"),
        arrivalAirport: getLegAirport(briefData, legIndex, "arrival"),
        departureTerminal: getLegTerminal(briefData, legIndex, "departure"),
        arrivalTerminal: getLegTerminal(briefData, legIndex, "arrival"),
    };
};

const getMapImage = (briefData, legOrIndex = 0) => {
    const { departureAirport, arrivalAirport, departureTerminal, arrivalTerminal } = getLegMapContext(
        briefData,
        legOrIndex
    );

    if (!departureAirport || !arrivalAirport) {
        return null;
    }

    const departureLatLng = getLocationLatLng(departureAirport, departureTerminal);
    const arrivalLatLng = getLocationLatLng(arrivalAirport, arrivalTerminal);

    if (!departureLatLng || !arrivalLatLng) {
        return null;
    }

    const line = GeoJSONPolyline.encode({
        type: "LineString",
        coordinates: [getLatLng(departureLatLng, true), getLatLng(arrivalLatLng, true)],
    });
    const encodedPolyline = encodeURIComponent(line.coordinates);

    const { lineColour, markerColour, width, height, padding, accessToken, showLogo } = mapboxConfig;

    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/path-1+${lineColour}(${encodedPolyline}),pin-s+${markerColour}(${getLatLng(
        departureLatLng
    )}),pin-s+${markerColour}(${getLatLng(arrivalLatLng)})/auto/${width}x${height}@2x?padding=${padding}&access_token=${accessToken}&logo=${showLogo}`;
};

const getMapAlt = (briefData, legOrIndex = 0) => {
    const { departureAirport, arrivalAirport } = getLegMapContext(briefData, legOrIndex);

    if (!departureAirport || !arrivalAirport) {
        return "";
    }

    const from = departureAirport.city || departureAirport.icao;
    const to = arrivalAirport.city || arrivalAirport.icao;

    return `Route map from ${from} to ${to}`;
};

const formatAirport = (airport) => {
    if (!airport) {
        return null;
    }

    return `${airport.icao} ${airport.city}, ${airport.country}`;
};

const getAirport = (briefData, mode, legIndex) => {
    const type = mode === "departureAirport" ? "departure" : "arrival";

    return formatAirport(getLegAirport(briefData, legIndex, type));
};

const getLegTitle = (index) => `Leg ${index + 1} - Departure & Arrival`;

const FlightBriefItinerary = ({ briefData }) => {
    if (!briefData?.legs?.length) {
        return null;
    }

    const isReturn = isReturnFlight(briefData);
    const isMultiLeg = briefData.legs.length > 1;
    const showTopLevelMap = isReturn || briefData.legs.length === 1;

    const renderLegBody = (leg, i) => {
        const departureAirport = getLegAirport(briefData, i, "departure");
        const arrivalAirport = getLegAirport(briefData, i, "arrival");
        const rawDistance = leg.distance ?? briefData.distance;
        // Some upstream values come through as e.g. "560NM" while others are bare numbers.
        // Strip any existing unit suffix so we always render exactly one " NM".
        const distance =
            rawDistance !== null && rawDistance !== undefined
                ? String(rawDistance).replace(/\s*nm\s*$/i, "")
                : rawDistance;

        return (
            <div className="flight-brief__content-grid">
                {departureAirport && (
                    <div>
                        <div className="flight-brief__gold-title">From</div>
                        <div className="flight-brief__dark-title">{getAirport(briefData, "departureAirport", i)}</div>
                        {leg.departureDate && leg.departureTime && (
                            <div className="flight-brief__body-text">
                                {leg.departureDate}
                                <br />
                                {leg.departureTime}
                            </div>
                        )}
                    </div>
                )}

                {arrivalAirport && (
                    <div>
                        <div className="flight-brief__gold-title">To</div>
                        <div className="flight-brief__dark-title">{getAirport(briefData, "arrivalAirport", i)}</div>
                        {leg.arrivalDate && leg.arrivalTime && (
                            <div className="flight-brief__body-text">
                                {leg.arrivalDate}
                                <br />
                                {leg.arrivalTime}
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <div className="flight-brief__gold-title">Details</div>
                    <div className="flight-brief__body-text">
                        {briefData.travelTime && leg.flightTime && (
                            <>
                                Duration: <b>{leg.flightTime}</b>
                                <br />
                            </>
                        )}
                        {distance && (
                            <>
                                Distance: <b>{distance} NM</b>
                                <br />
                            </>
                        )}
                        {briefData.numberPax && (
                            <>
                                Pax: <b>{briefData.numberPax}</b>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flight-brief__itinerary-wrapper">
            {showTopLevelMap && (
                <figure className="flight-brief__map">
                    <img src={getMapImage(briefData)} alt={getMapAlt(briefData)} />
                </figure>
            )}

            <div
                className={`flight-brief__itinerary-content${
                    !isReturn && isMultiLeg ? " flight-brief__itinerary-content--full" : ""
                }`}
            >
                {briefData.legs.map((leg, i) => {
                    const title = isMultiLeg && <p className="flight-brief__group-title">{getLegTitle(i)}</p>;

                    if (isReturn || !isMultiLeg) {
                        return (
                            <Fragment key={i}>
                                {title}
                                {renderLegBody(leg, i)}
                            </Fragment>
                        );
                    }

                    const mapImageUrl = getMapImage(briefData, i);

                    return (
                        <div className="flight-brief__itinerary-row" key={i}>
                            {title}
                            <div className="flight-brief__itinerary-row-inner">
                                {mapImageUrl && (
                                    <figure className="flight-brief__map">
                                        <img src={mapImageUrl} alt={getMapAlt(briefData, i)} />
                                    </figure>
                                )}
                                {renderLegBody(leg, i)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FlightBriefItinerary;
